import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Flag, Lock, Trash2, Copy, FileText, PenLine, History, Mail, Download } from 'lucide-react';
import api from '../../../utils/api';
import {
  Panel, SearchInput, Button, Pill, RowMenu, Tip, Empty, Banner, ConfirmModal, LoadingBlock, ErrorBlock,
  money, useLoad, fmtDate, hoursOf,
} from '../kit';
import { flagText } from './TimeEntryForm';
import { useLogTime, useOnTimeSaved } from './LogTimeProvider';
import { ViewPicker, asTimeEntry, fullName } from '../file/shared';
import { LockBadge, EditedLine, NoteHistoryModal, payLabel, payTone } from './timeLock';

// Notes & Billing: the participant's time entries as a table, newest first, in month groups with
// each month's hours and amount. Every hour a coordinator is paid for and every claim line comes
// from these, so each row says where its time has gone (SC hours, claim). A row opens the entry in
// the one Log time sheet. Phones get the same entries as stacked rows. A padlock marks an entry whose
// time is locked (claim file or invoice downloaded or sent, or pay batch paid); its case note stays
// editable, and an edited note shows who edited it last with a History link.

// Pay statuses: Not paid, Billed (contractors) or Exported to payroll (employees), Paid. Notes from
// before SC Hours ('legacy') carry no pay status of their own, so the row shows only the claim.
const payState = (e) => (e.pay_status && e.pay_status !== 'legacy'
  ? { label: `Pay: ${payLabel(e.pay_status, e.pay_status_label)}`, tone: payTone(e.pay_status) } : null);
const claimState = (e) => {
  if (!e.billable) return null;
  if (e.claim_line_id) return { label: 'In a claim', tone: 'violet' };
  if (e.claim_id) return { label: 'Earlier claim', tone: 'slate' };
  return { label: 'Not yet claimed', tone: 'blue' };
};
const isUnclaimed = (e) => e.billable && !e.claim_line_id && !e.claim_id;
const noTimes = (e) => !e.start_time && !e.end_time && e.source !== 'adjustment';
// Activity names are the company's own words; a code someone typed into one stays out of sight.
const activityText = (e) => {
  const n = String(e.activity_name || '').replace(/\s*\([A-Z0-9]{3,5}\)\s*$/, '').trim();
  if (n) return n;
  return noTimes(e) ? 'Note (no times)' : 'Time entry';
};
const utcDate = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(Date.UTC(y, m - 1, d)) : null;
};
const shortDay = (iso) => {
  const d = utcDate(iso);
  return d ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : fmtDate(iso);
};
const longDay = (iso) => {
  const d = utcDate(iso);
  return d ? d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : fmtDate(iso);
};
const monthName = (key) => {
  const d = utcDate(`${key}-01`);
  return d ? d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : key;
};
const totalsOf = (list) => ({
  count: list.length,
  minutes: list.reduce((t, r) => t + (Number(r.minutes_rounded) || 0), 0),
  billable: list.filter((r) => r.billable).reduce((t, r) => t + (Number(r.line_total) || 0), 0),
  unclaimed: list.filter(isUnclaimed).reduce((t, r) => t + (Number(r.line_total) || 0), 0),
});
const entriesText = (n) => `${n} ${n === 1 ? 'entry' : 'entries'}`;

export default function ParticipantNotes({ coordinationId, clientId, participantName, budgets = [], services = [], perms = [], onFromEmail }) {
  const logTime = useLogTime();
  const navigate = useNavigate();
  const l = useLoad(coordinationId ? '/sc/time-entries' : null, { coordination_id: coordinationId });
  useOnTimeSaved(l.reload);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [removing, setRemoving] = useState(null);
  const [history, setHistory] = useState(null);
  const canLog = perms.includes('log_time') || perms.includes('view_all_hours');
  const ownOnly = !['view_all_hours', 'claiming'].some((p) => perms.includes(p));
  const rows = useMemo(() => l.data || [], [l.data]);
  // The Time and Case Notes report, pre-filtered to this participant.
  const reportClientId = clientId || (rows.find((r) => r.client_id) || {}).client_id || null;
  const openReport = () => navigate(`/support-coordination?view=dashboard&tab=reports&report=time-case-notes&client_ids=${encodeURIComponent(reportClientId)}`);

  const counts = useMemo(() => ({
    unclaimed: rows.filter(isUnclaimed).length,
    flagged: rows.filter((r) => Array.isArray(r.flags) && r.flags.length).length,
    locked: rows.filter((r) => r.locked).length,
    noTimes: rows.filter(noTimes).length,
  }), [rows]);

  // The entries in view, newest first, grouped by month; firstOfDay marks where a new day starts.
  const { months, shown } = useMemo(() => {
    let out = rows;
    if (filter === 'unclaimed') out = out.filter(isUnclaimed);
    if (filter === 'flagged') out = out.filter((r) => Array.isArray(r.flags) && r.flags.length);
    if (filter === 'locked') out = out.filter((r) => r.locked);
    const s = q.trim().toLowerCase();
    if (s) out = out.filter((r) => `${r.summary || ''} ${r.activity_name || ''} ${r.coordinator_first || ''} ${r.coordinator_last || ''}`.toLowerCase().includes(s));
    const sorted = [...out].sort((a, b) => String(b.note_date).localeCompare(String(a.note_date)) || String(b.start_time || '').localeCompare(String(a.start_time || '')));
    const groups = [];
    let lastDay = null;
    sorted.forEach((r) => {
      const day = String(r.note_date || '').slice(0, 10);
      const key = day.slice(0, 7);
      let g = groups[groups.length - 1];
      if (!g || g.key !== key) { g = { key, items: [] }; groups.push(g); }
      g.items.push({ e: r, firstOfDay: day !== lastDay });
      lastDay = day;
    });
    return { months: groups.map((g) => ({ ...g, totals: totalsOf(g.items.map((i) => i.e)) })), shown: sorted };
  }, [rows, filter, q]);
  const total = useMemo(() => totalsOf(shown), [shown]);

  // The server's lock decides: an entry in a claim batch that hasn't been downloaded yet still edits.
  const open = (e) => logTime({ coordinationId, participantName, budgets, services, entry: { ...asTimeEntry(e), locked: !!e.locked } });
  const remove = async () => {
    await api.delete(`/sc/time-entries/${removing.id}`);
    toast.success('Time entry removed');
    l.reload();
  };
  const copy = async (e) => {
    const text = `${participantName}\n${fmtDate(e.note_date)} ${e.start_time || ''}${e.end_time ? `–${e.end_time}` : ''} · ${activityText(e)} · ${hoursOf(e.minutes_rounded)} h\n\n${e.summary || ''}`;
    try { await navigator.clipboard.writeText(text); toast.success('Note copied'); } catch { toast.error('Copy didn’t work. Your browser blocked the clipboard.'); }
  };
  const menu = (e) => [
    { label: e.locked ? 'Open (time locked, note editable)' : 'Open and edit', icon: e.locked ? Lock : PenLine, onClick: () => open(e) },
    { label: 'Copy note', icon: Copy, onClick: () => copy(e) },
    !e.locked && e.pay_status !== 'legacy' && 'sep',
    !e.locked && e.pay_status !== 'legacy' && { label: 'Remove', icon: Trash2, danger: true, onClick: () => setRemoving(e) },
  ];

  const filters = [
    { value: 'all', label: 'All', count: rows.length },
    { value: 'unclaimed', label: 'Not yet claimed', count: counts.unclaimed },
    { value: 'flagged', label: 'Flagged', count: counts.flagged, alert: counts.flagged > 0 },
    { value: 'locked', label: 'Locked', count: counts.locked },
  ];

  return (
    <Panel pad={false}>
      <div className="px-4 sm:px-5 pt-4 pb-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <ViewPicker label="Show entries" value={filter} onChange={setFilter} options={filters} />
          <SearchInput value={q} onChange={setQ} placeholder="Search notes" className="w-full sm:w-56" />
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            {canLog && onFromEmail && <Button variant="ghost" size="sm" icon={Mail} onClick={onFromEmail}>Note from an email</Button>}
            {reportClientId && (
              <Button variant="secondary" size="sm" icon={Download} onClick={openReport}
                aria-label={`Download the time and case notes report for ${participantName || 'this participant'}`}>Download</Button>
            )}
          </div>
        </div>
        {shown.length > 0 && (
          <p className="text-[12.5px] text-slate-600" aria-live="polite">
            <span className="font-semibold text-slate-800 tabular-nums">{entriesText(total.count)}</span>
            {' · '}<span className="font-semibold text-slate-800 tabular-nums">{hoursOf(total.minutes)} h</span>
            {' · '}<span className="font-semibold text-slate-800 tabular-nums">{money(total.billable)}</span> billable
            {total.unclaimed > 0 && <>{' · '}<span className="font-semibold text-sky-800 tabular-nums">{money(total.unclaimed)}</span> not yet claimed</>}
          </p>
        )}
        {ownOnly && <p className="text-[12.5px] text-slate-500">Showing your own time. People with View all hours see everyone’s.</p>}
        {counts.noTimes > 0 && (
          <Banner tone="amber">
            {counts.noTimes === 1 ? '1 note has' : `${counts.noTimes} notes have`} no start and end time, so {counts.noTimes === 1 ? 'it isn’t' : 'they aren’t'} in SC Hours. Open one and add its times.
          </Banner>
        )}
      </div>

      {l.error ? <div className="px-4 sm:px-5 pb-4"><ErrorBlock error={l.error} onRetry={l.reload} /></div>
        : !l.data ? <div className="px-4 sm:px-5 pb-4"><LoadingBlock rows={5} /></div>
        : !months.length ? (
          <div className="px-4 sm:px-5 pb-4">
            <Empty icon={FileText} title={rows.length ? 'No entries match' : 'No time logged yet'}
              action={rows.length > 0 && (filter !== 'all' || q) ? <Button variant="secondary" size="sm" onClick={() => { setFilter('all'); setQ(''); }}>Show all entries</Button> : null}>
              {rows.length ? 'Try another view or clear the search.' : 'Use Log time at the top of the file to record a call, meeting or email for this participant.'}
            </Empty>
          </div>
        ) : (
          <>
            <NotesTable months={months} total={total} participantName={participantName} onOpen={open} menu={menu} onHistory={setHistory} />
            <div className="md:hidden border-t border-slate-100">
              {months.map((m) => (
                <section key={m.key} aria-label={monthName(m.key)}>
                  <MonthBand month={m} />
                  <ul className="divide-y divide-slate-100">
                    {m.items.map(({ e }) => <EntryCard key={e.id} e={e} onOpen={open} menu={menu} onHistory={setHistory} />)}
                  </ul>
                </section>
              ))}
            </div>
          </>
        )}
      {l.loading && l.data && <p className="px-5 pb-3 text-[12px] text-slate-500" aria-live="polite">Refreshing…</p>}

      <ConfirmModal open={!!removing} onClose={() => setRemoving(null)} title="Remove this time entry?" danger confirmLabel="Remove entry"
        onConfirm={remove}>
        It stops counting toward SC hours and claims. The change is kept in the audit trail.
      </ConfirmModal>
      <NoteHistoryModal entry={history} onClose={() => setHistory(null)} />
    </Panel>
  );
}

// The month heading: name on the left, that month's entries, hours and amount on the right.
function MonthBand({ month, asCell }) {
  const t = month.totals;
  const inner = (
    <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
      <span className="text-[13px] font-semibold text-violet-950">{monthName(month.key)}</span>
      <span className="text-[12px] text-violet-900/80 tabular-nums">
        {entriesText(t.count)} · {hoursOf(t.minutes)} h · {money(t.billable)}
      </span>
    </span>
  );
  if (asCell) return inner;
  return <div className="bg-violet-50/80 border-b border-violet-100 px-4 py-2">{inner}</div>;
}

const TH = 'bg-slate-50 text-[12px] font-semibold text-slate-600 border-y border-slate-200/80 px-3 py-2.5 text-left first:pl-5 last:pr-4';
const TD = 'px-3 py-3 first:pl-5 last:pr-4 border-b border-slate-100 align-top';

function NotesTable({ months, total, participantName, onOpen, menu, onHistory }) {
  return (
    // Clipped to the panel's bottom corners so the totals row follows its rounding.
    <div className="hidden md:block overflow-x-auto rounded-b-2xl">
      <table className="w-full min-w-[720px] text-[13px] border-separate border-spacing-0">
        <caption className="sr-only">Time entries for {participantName}, newest first, grouped by month</caption>
        <thead>
          <tr>
            <th scope="col" className={TH} style={{ width: 112 }}>Date</th>
            <th scope="col" className={TH} style={{ width: 112 }}>Time</th>
            <th scope="col" className={TH}>Activity and note</th>
            <th scope="col" className={clsx(TH, 'hidden xl:table-cell')} style={{ width: 150 }}>Coordinator</th>
            <th scope="col" className={clsx(TH, 'text-right')} style={{ width: 76 }}>Hours</th>
            <th scope="col" className={clsx(TH, 'text-right')} style={{ width: 100 }}>Amount</th>
            <th scope="col" className={TH} style={{ width: 168 }}>Status</th>
            <th scope="col" className={TH} style={{ width: 52 }}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        {months.map((m) => (
          <tbody key={m.key}>
            <tr>
              <th scope="rowgroup" colSpan={8} className="bg-violet-50/80 border-b border-violet-100 px-5 py-2 text-left font-normal">
                <MonthBand month={m} asCell />
              </th>
            </tr>
            {m.items.map(({ e, firstOfDay }) => <EntryRow key={e.id} e={e} firstOfDay={firstOfDay} onOpen={onOpen} menu={menu} onHistory={onHistory} />)}
          </tbody>
        ))}
        <tfoot>
          <tr className="bg-slate-50 text-slate-900">
            <td colSpan={3} className="px-3 pl-5 py-3 font-semibold border-t border-slate-200">
              Total <span className="font-normal text-slate-600">· {entriesText(total.count)}</span>
            </td>
            <td className="hidden xl:table-cell border-t border-slate-200" />
            <td className="px-3 py-3 text-right font-semibold tabular-nums border-t border-slate-200">{hoursOf(total.minutes)}</td>
            <td className="px-3 py-3 text-right font-semibold tabular-nums border-t border-slate-200">{money(total.billable)}</td>
            <td colSpan={2} className="px-3 pr-4 py-3 text-[12px] text-slate-600 border-t border-slate-200">
              {total.unclaimed > 0 ? <><span className="font-semibold text-sky-800 tabular-nums">{money(total.unclaimed)}</span> not yet claimed</> : 'All claimed'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ActivityTitle({ e }) {
  const flags = Array.isArray(e.flags) ? e.flags : [];
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      {e.source === 'adjustment' && <><History size={13} className="text-violet-600 flex-shrink-0" aria-hidden /><span className="sr-only">Adjustment: </span></>}
      <span className="min-w-0 break-words">{activityText(e)}</span>
      <LockBadge entry={e} />
      {flags.length > 0 && (
        <Tip content={flags.map(flagText).join(' · ')}>
          <span className="inline-flex text-amber-600"><Flag size={13} aria-hidden /><span className="sr-only">, flagged: {flags.map(flagText).join('; ')}</span></span>
        </Tip>
      )}
    </span>
  );
}

function StatusPills({ e }) {
  const pay = payState(e);
  const claim = claimState(e);
  if (!pay && !claim) return <span className="text-[12px] text-slate-500">Not billable</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {claim && <Pill size="xs" tone={claim.tone} label={claim.label} />}
      {pay && <Pill size="xs" tone={pay.tone} label={pay.label} />}
    </span>
  );
}

function EntryRow({ e, firstOfDay, onOpen, menu, onHistory }) {
  const who = fullName(e.coordinator_first, e.coordinator_last);
  const mins = Number(e.minutes_rounded) || 0;
  return (
    <tr onClick={() => onOpen(e)} className="group cursor-pointer transition-colors hover:bg-indigo-50/40">
      <td className={clsx(TD, 'whitespace-nowrap')}>
        {firstOfDay
          ? <span className="font-semibold text-slate-800" title={longDay(e.note_date)}>{shortDay(e.note_date)}</span>
          : <span className="sr-only">{shortDay(e.note_date)}</span>}
      </td>
      <td className={clsx(TD, 'whitespace-nowrap tabular-nums')}>
        {e.start_time ? <span className="text-slate-700">{e.start_time}–{e.end_time || ''}</span> : <span className="font-semibold text-amber-800">No times</span>}
      </td>
      <td className={TD}>
        <button type="button" onClick={(ev) => { ev.stopPropagation(); onOpen(e); }}
          className="text-left font-semibold text-slate-900 rounded hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
          <ActivityTitle e={e} />
        </button>
        {e.summary && <p className="mt-0.5 text-slate-600 leading-relaxed line-clamp-2 max-w-[70ch]">{e.summary}</p>}
        {e.note_edited_at && <p className="mt-0.5"><EditedLine entry={e} onHistory={onHistory} /></p>}
        {who && <p className="xl:hidden mt-1 text-[12px] text-slate-500">{who}</p>}
      </td>
      <td className={clsx(TD, 'hidden xl:table-cell text-slate-700')}>{who || '—'}</td>
      <td className={clsx(TD, 'text-right font-semibold tabular-nums', mins < 0 ? 'text-rose-700' : 'text-slate-900')}>{hoursOf(mins)}</td>
      <td className={clsx(TD, 'text-right tabular-nums')}>
        {e.billable ? <span className="text-slate-900">{money(e.line_total)}</span> : <span className="text-slate-500">—</span>}
      </td>
      <td className={TD}><StatusPills e={e} /></td>
      <td className={clsx(TD, 'text-right')} onClick={(ev) => ev.stopPropagation()}>
        <span className="inline-flex -my-1.5"><RowMenu label={`More actions for the ${fmtDate(e.note_date)} entry`} items={menu(e)} /></span>
      </td>
    </tr>
  );
}

// Phones: one stacked row per entry.
function EntryCard({ e, onOpen, menu, onHistory }) {
  const who = fullName(e.coordinator_first, e.coordinator_last);
  const mins = Number(e.minutes_rounded) || 0;
  return (
    <li className="flex items-start gap-1 px-2">
      <div className="flex-1 min-w-0">
      <button type="button" onClick={() => onOpen(e)}
        className="w-full text-left rounded-xl px-2 py-3 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 transition-colors">
        {/* Spans only: a button may hold phrasing content, not divs or paragraphs. */}
        <span className="flex items-baseline justify-between gap-3 text-[12.5px] tabular-nums">
          <span className="font-semibold text-slate-700">
            {shortDay(e.note_date)}{' · '}
            {e.start_time ? `${e.start_time}–${e.end_time || ''}` : <span className="text-amber-800">No times</span>}
          </span>
          <span className={clsx('font-semibold', mins < 0 ? 'text-rose-700' : 'text-slate-900')}>{hoursOf(mins)} h</span>
        </span>
        <span className="mt-1 flex text-[14px] font-semibold text-slate-900"><ActivityTitle e={e} /></span>
        {e.summary && <span className="block mt-0.5 text-[13px] leading-relaxed text-slate-600 line-clamp-2">{e.summary}</span>}
        <span className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-slate-500">
          {who && <span>{who}</span>}
          {who && <span aria-hidden>·</span>}
          <span className="tabular-nums">{e.billable ? money(e.line_total) : 'Not billable'}</span>
          {(payState(e) || claimState(e)) && <StatusPills e={e} />}
        </span>
      </button>
      {e.note_edited_at && <p className="px-2 -mt-2 pb-3"><EditedLine entry={e} onHistory={onHistory} /></p>}
      </div>
      <div className="pt-2">
        <RowMenu label={`More actions for the ${fmtDate(e.note_date)} entry`} items={menu(e)} />
      </div>
    </li>
  );
}
